import { defineComponent, ref, computed } from 'vue';
import TIcon from './../t-icon/index';
import TButton from './../t-button/index';
import TDivider from './../t-divider/index';
export default defineComponent({
    name: 'TActionSheet',
    components: { TIcon, TButton, TDivider },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        maskClosable: {
            type: Boolean,
            default: true,
        },
        isNeedClose: {
            type: Boolean,
            default: false,
        },
        /**
         * 是否自动关闭
         * 在某些特殊场景下，不是组件内部自己关闭的
         * 而在特定的业务逻辑实现之后，由调用方自己主动关闭的场景
         */
        isConfirmClose: {
            type: Boolean,
            default: true,
        },
        isCancelClose: {
            type: Boolean,
            default: true,
        },
        isAutoClose: {
            type: Boolean,
            default: true,
        },
        hasAnimate: {
            type: Boolean,
            default: true,
        },
        confirmTitle: {
            type: String,
            default: '',
        },
        cancelTitle: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        subtitleIcon: {
            type: String,
            default: '',
        },
        theme: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'error'].indexOf(value) !== -1,
        },
        transparent: {
            type: Boolean,
            default: false,
        },
        height: {
            type: [Number, String],
            default: 0,
        },
    },
    emits: ['click', 'update:modelValue', 'close', 'cancel', 'confirm'],
    setup(props, context) {
        /** 组件的className前辍 */
        const prefixCls = ref('t-action-sheet');
        /**
         * 格式化右上角的关闭按钮
         * 如果有cancelTitle或者confirmTitle的情况下，不能出现关闭的icon
         */
        const getFormatCloseInfo = computed(() => {
            const isHasTitle = context.slots.header || props.title;
            return (isHasTitle || props.isNeedClose) && !props.cancelTitle && !props.confirmTitle;
        });
        /** 动态设置底部按钮的颜色外观 */
        const getButtonTheme = computed(() => (props.theme === 'default' ? 'text' : 'error'));
        /** 模板依赖的className */
        const getClasses = computed(() => {
            return {
                wrap: [prefixCls.value],
                header: [`${prefixCls.value}-hd`],
                subtitleGroup: [`${prefixCls.value}-subtitle-group`],
                subtitleIcon: [`${prefixCls.value}-subicon`],
                mask: [
                    `${prefixCls.value}-mask`,
                    !props.transparent && `${prefixCls.value}-mask__default`,
                    props.hasAnimate && `${prefixCls.value}-mask__animate`,
                ],
                title: [`${prefixCls.value}-title`, props.subtitle && `${prefixCls.value}-title__margin`],
                subtitle: [`${prefixCls.value}-subtitle`, props.theme === 'error' && `${prefixCls.value}-subtitle__error`],
                inner: [`${prefixCls.value}-inner`, props.hasAnimate && `${prefixCls.value}-inner__animate`],
                body: [`${prefixCls.value}-bd`, props.height === 0 && `${prefixCls.value}-bd__default`],
                closeWrap: [`${prefixCls.value}-close`],
                cancel: [`${prefixCls.value}-cancel`],
                confirm: [`${prefixCls.value}-confirm`],
            };
        });
        /** 对外响应的事件 */
        const listener = (event) => context.emit('click', event);
        /** 关闭整个action-sheet */
        const onClose = (event) => {
            if (props.isAutoClose) {
                context.emit('update:modelValue', false);
            }
            context.emit('close', event);
        };
        /**
         * 取消action-sheet
         * 相当于关闭了action-sheet
         */
        const onCancel = (event) => {
            if (props.isCancelClose) {
                context.emit('update:modelValue', false);
            }
            context.emit('cancel', event);
        };
        /**
         * 确认action-sheet
         * 相当于关闭了action-sheet
         */
        const onConfirm = (event) => {
            if (props.isConfirmClose) {
                context.emit('update:modelValue', false);
            }
            context.emit('confirm', event);
        };
        /** 点击遮罩层的关闭事件 */
        const onTapByMask = (event) => props.maskClosable && onClose(event);
        /** 动态设置组件的高度 */
        const styles = computed(() => {
            if (typeof props.height === 'string') {
                if (/^[\d|.]*$/.test(props.height)) {
                    return {
                        height: `${props.height}px`,
                    };
                }
                return {
                    height: props.height,
                };
            }
            if (props.height && typeof props.height === 'number' && !isNaN(props.height)) {
                return {
                    height: `${props.height}px`,
                };
            }
            return {};
        });
        return {
            prefixCls,
            getClasses,
            getFormatCloseInfo,
            getButtonTheme,
            listener,
            onClose,
            styles,
            onCancel,
            onConfirm,
            onTapByMask,
        };
    },
});
